import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Chip,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/joy';
import { FaPlus, FaSearch, FaFileInvoice, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { documentsService } from '../modules/docs/services/documents.service';
import { documentLinkingService } from '../services/documentLinkingService';
import { matchesLinkableSearch } from '../services/documentLinkingSearch';

interface InvoiceLinkerProps {
  projectId: string;
  onLinkCreated?: () => void;
  hideHeader?: boolean;
  hideLinkedInvoices?: boolean;
}

export const InvoiceLinker = forwardRef<{ openModal: () => void }, InvoiceLinkerProps>(({ 
  projectId, 
  onLinkCreated,
  hideHeader = false,
  hideLinkedInvoices = false
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Expose openModal function to parent component
  const openModal = () => {
    setIsSearching(true);
    setIsOpen(true);
  };
  
  // Make openModal available to parent via ref
  useImperativeHandle(ref, () => ({
    openModal
  }));

  type LinkerInvoice = {
    id: string;
    document_ref: string;
    title: string;
    invoice_number: string;
    customer_first_name: string;
    customer_last_name: string;
    grand_total: number;
    status: string;
    created_at: string;
    payment_status: string;
    has_deposit: boolean;
    deposit_amount: number | null;
    deposit_percentage: number | null;
    projectId: string | null;
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [allInvoices, setAllInvoices] = useState<LinkerInvoice[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setCurrentPage(1);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsSearching(true);
      setSearchError(null);
      try {
        const linkableDocs = await documentLinkingService.getAllLinkableDocuments();
        if (cancelled) return;
        setAllInvoices(
          linkableDocs
            .filter((doc) => doc.type === 'Invoice' && !doc.projectId)
            .map((doc) => {
              const parts = (doc.customerName || '').split(' ').filter(Boolean);
              return {
                id: doc.id,
                document_ref: doc.reference || '',
                title: doc.title || '',
                invoice_number: doc.reference || '',
                customer_first_name: parts[0] || '',
                customer_last_name: parts.slice(1).join(' '),
                grand_total: doc.amount || 0,
                status: doc.status || 'draft',
                created_at: doc.createdAt || new Date().toISOString(),
                payment_status: (doc.status === 'paid' || doc.status === 'cash') ? 'paid' : doc.status === 'void' ? 'void' : 'unpaid',
                has_deposit: false,
                deposit_amount: null,
                deposit_percentage: null,
                projectId: doc.projectId ?? null,
              };
            })
        );
      } catch (err) {
        console.error('Error searching invoices:', err);
        if (!cancelled) setSearchError('An error occurred while searching for invoices.');
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId]);

  const searchResults = useMemo(
    () =>
      allInvoices.filter((invoice) =>
        matchesLinkableSearch(
          {
            reference: invoice.document_ref,
            title: invoice.title,
            customerFirstName: invoice.customer_first_name,
            customerLastName: invoice.customer_last_name,
            kind: 'Invoice',
          },
          searchQuery
        )
      ),
    [allInvoices, searchQuery]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const totalPages = Math.max(1, Math.ceil(searchResults.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentResults = searchResults.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleCreateLink = async (invoiceId: string) => {
    try {
      setLoading(true);
      setError(null);
      const invoice = allInvoices.find((inv) => inv.id === invoiceId);
      if (!invoice?.id) throw new Error('Invoice not found');
      await documentsService.updateDocument(invoice.id, { projectId });
      setIsOpen(false);
      setSearchQuery('');
      setAllInvoices([]);
      onLinkCreated?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create link';
      setError(errorMessage);
      console.error('InvoiceLinker - error creating link:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getPaymentStatusColor = (status?: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'unpaid': return 'danger';
      case 'partial': return 'warning';
      case 'overdue': return 'danger';
      default: return 'neutral';
    }
  };

  const getPaymentStatusText = (status?: string) => {
    switch (status) {
      case 'paid': return 'Paid';
      case 'unpaid': return 'Unpaid';
      case 'partial': return 'Partial';
      case 'overdue': return 'Overdue';
      default: return 'Unknown';
    }
  };

  return (
    <Box>
      {/* Header with Add Link button */}
      {!hideHeader && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography level="title-md">Invoices</Typography>
          <Button
            size="sm"
            startDecorator={<IconWrapper icon={FaPlus} />}
            onClick={() => openModal()}
          >
            Link Invoice
          </Button>
        </Box>
      )}

      {/* Error display */}
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search Modal */}
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <ModalDialog 
          size="lg" 
          sx={{ 
            maxHeight: { xs: 'calc(90vh - 56px - env(safe-area-inset-top, 0px))', sm: '90vh' },
            width: { xs: '95vw', sm: '80vw', md: '70vw' },
            maxWidth: '800px',
            overflow: 'hidden', 
            display: 'flex', 
            flexDirection: 'column',
            m: { xs: 1, sm: 2 },
            // Account for mobile header
            mt: { xs: 'calc(56px + env(safe-area-inset-top, 0px) + 8px)', sm: 2 },
            // Ensure modal appears above header
            zIndex: 1400
          }}
        >
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Link Invoice
          </Typography>

          {/* Search Section */}
          <Box sx={{ mb: 2 }}>
            <Typography level="body-sm" sx={{ mb: 1 }}>
              Search invoices by number, title, or customer
            </Typography>
            <Input
              placeholder="Try a name, title, or KSG reference…"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              startDecorator={isSearching ? <CircularProgress size="sm" /> : <IconWrapper icon={FaSearch} />}
              sx={{ mb: 2 }}
            />
          </Box>

          {searchError && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {searchError}
            </Alert>
          )}

          {/* Results Section */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Typography level="title-sm" sx={{ mb: 2 }}>
              Available Invoices ({searchResults.length})
            </Typography>
            {isSearching && allInvoices.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {!isSearching && searchResults.length === 0 && (
              <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
                {searchQuery.trim()
                  ? `No invoices match “${searchQuery.trim()}”.`
                  : 'No unlinked invoices found.'}
              </Typography>
            )}
            <List sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
              {currentResults.map((invoice) => (
                <ListItem
                  key={invoice.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 'sm',
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'background.level1'
                    }
                  }}
                >
                  <ListItemDecorator>
                    <IconWrapper icon={FaFileInvoice} />
                  </ListItemDecorator>
                  
                  <ListItemContent>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'flex-start' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Document ID - bold, top */}
                        <Typography level="title-sm" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          {invoice.invoice_number || invoice.document_ref || invoice.id}
                        </Typography>
                        
                        {/* Customer name - below, smaller */}
                        <Typography level="body-sm" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: 'neutral.600' }}>
                          {invoice.customer_first_name && invoice.customer_last_name 
                            ? `${invoice.customer_first_name} ${invoice.customer_last_name}` 
                            : 'Unknown Customer'}
                        </Typography>
                        
                        {/* Amount and status - below */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 0.5, sm: 1 }, 
                          alignItems: { xs: 'flex-start', sm: 'center' }, 
                          mt: 0.5 
                        }}>
                          <Typography level="body-sm" fontWeight="medium" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            {formatCurrency(invoice.grand_total)}
                          </Typography>
                          <Chip 
                            size="sm" 
                            variant="soft" 
                            color={getPaymentStatusColor(invoice.status)}
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {getPaymentStatusText(invoice.status) || `Status: ${invoice.status || 'unknown'}`}
                          </Chip>
                        </Box>
                      </Box>
                      
                      {/* Link Document button */}
                      <Button
                        size="sm"
                        onClick={() => handleCreateLink(invoice.id)}
                        disabled={loading}
                        sx={{ 
                          minWidth: { xs: '100%', sm: 'auto' },
                          mt: { xs: 1, sm: 0 }
                        }}
                      >
                        Link Document
                      </Button>
                    </Box>
                  </ListItemContent>
                </ListItem>
              ))}
            </List>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: 1,
                py: 1,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <IconButton
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <IconWrapper icon={FaChevronLeft} />
                </IconButton>
                
                <Typography level="body-sm">
                  Page {currentPage} of {totalPages}
                </Typography>
                
                <IconButton
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <IconWrapper icon={FaChevronRight} />
                </IconButton>
              </Box>
            )}
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
}); 